import { useEffect, useRef } from "react";
import type { WorkflowSectionContext } from "./types";

/** The conversation a workflows panel shows, as one comparable key. */
export function conversationKey(scope: WorkflowSectionContext["scope"]): string {
  return JSON.stringify([scope.orgId, scope.projectId, scope.userId, scope.threadId]);
}

/**
 * A workflows side panel belongs to the conversation it was opened in: switching
 * conversations closes it, so one thread's panel never shows over another. Escape
 * closes it too, unless something above it (an image dialog) took the key first.
 */
export function useConversationPanel(ctx: WorkflowSectionContext): void {
  const key = conversationKey(ctx.scope);
  const close = useRef(ctx.setPanelOpen);
  close.current = ctx.setPanelOpen;

  const shownFor = useRef(key);
  useEffect(() => {
    if (shownFor.current === key) return;
    shownFor.current = key;
    if (ctx.panelOpen) close.current(false);
    // Only the conversation changing closes it; opening is the user's or the section's.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    if (!ctx.panelOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !event.defaultPrevented) close.current(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [ctx.panelOpen]);
}
